import React from 'react';
import { Card, CardBody, Button } from 'reactstrap';
import { useHistory } from 'react-router-dom';
import useRoles from '~/hooks/useRoles';

/**
 * Card exibido no detalhe da venda quando a venda foi criada via API
 * (origin_partner_application_id preenchido).
 *
 * Visível apenas para usuários admin ou support — operadores comuns
 * (vendedores, consultoras) não precisam ver essa informação técnica.
 *
 * Conteúdo:
 *   - Nome da aplicação parceira que criou a venda
 *   - Badge do environment (teste/produção)
 *   - ID da aplicação (útil pra suporte rastrear)
 *   - Link "Ver aplicação" → abre detalhe no painel admin
 *   - Link "Ver webhooks" → abre dispatches filtrados pela aplicação
 *
 * Se a aplicação foi removida (origin_partner_application = null mas id presente),
 * mostra mensagem indicando a remoção sem quebrar a UI.
 */
export default function ApiOriginInfoCard({ cart }) {
  const history = useHistory();
  const { isSupport } = useRoles();

  // Não renderiza pra usuários comuns
  if (!isSupport) return null;

  // Não renderiza se não for venda via API
  if (!cart?.origin_partner_application_id) return null;

  const partnerApp =
    cart?.origin_partner_application || cart?.originPartnerApplication;
  const appName = partnerApp?.name || null;
  const appId = cart?.origin_partner_application_id;
  const appEnv = partnerApp?.environment || null;

  // Só considera "removida" quando a API retornou a chave explicitamente
  // como null. Se a chave nem veio no payload (undefined), a relação não
  // foi carregada pelo backend — não podemos afirmar que foi removida.
  const relationKeyPresent =
    cart != null &&
    (Object.prototype.hasOwnProperty.call(cart, 'origin_partner_application') ||
      Object.prototype.hasOwnProperty.call(cart, 'originPartnerApplication'));
  const appWasRemoved =
    Boolean(partnerApp?.removed) || (relationKeyPresent && !partnerApp);
  const isLiveEnv = appEnv === 'live' || appEnv === 'production';

  const handleViewApp = () => {
    history.push(`/aplicacoes-parceiras/${appId}/editar`);
  };

  const handleViewWebhooks = () => {
    history.push(`/webhooks-dispatches?partner_application_id=${appId}`);
  };

  return (
    <Card className="new-layout card">
      <CardBody className="new-layout card-body px-3 py-3">
        <div className="d-flex align-items-center mb-2">
          <i
            className="las la-plug mr-2"
            style={{ fontSize: '1.5rem', color: '#3b82f6' }}
          />
          <span className="font-size-16 font-weight-600 text-gray-700">
            Venda criada via API
          </span>
        </div>

        <div className="mt-3">
          <div className="font-size-14 font-weight-600 text-gray-600">
            Aplicação
          </div>
          <div className="font-size-14 font-weight-400 d-flex align-items-center flex-wrap">
            {appName ? (
              <>
                <span className="mr-2">{appName}</span>
                {appEnv && (
                  <span
                    className="px-2 py-1"
                    style={{
                      backgroundColor: isLiveEnv ? '#dc2626' : '#9ca3af',
                      color: 'white',
                      fontSize: '0.65rem',
                      borderRadius: '4px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {isLiveEnv ? 'Produção' : 'Teste'}
                  </span>
                )}
              </>
            ) : (
              <span className="text-muted font-italic">
                {appWasRemoved
                  ? 'Aplicação removida do sistema'
                  : 'Não identificada'}
              </span>
            )}
          </div>
        </div>

        <div className="mt-2">
          <div className="font-size-14 font-weight-600 text-gray-600">
            ID da aplicação
          </div>
          <code className="font-size-12 text-muted">{appId}</code>
        </div>

        <div className="mt-3 d-flex flex-wrap" style={{ gap: '0.5rem' }}>
          {!appWasRemoved && (
            <Button
              size="sm"
              color="info"
              outline
              className="new-layout-btn main d-inline-flex align-items-center"
              onClick={handleViewApp}
            >
              <i className="las la-external-link-alt mr-1 font-size-14" />
              Ver aplicação
            </Button>
          )}
          <Button
            size="sm"
            color="info"
            outline
            className="new-layout-btn main d-inline-flex align-items-center"
            onClick={handleViewWebhooks}
          >
            <i className="las la-paper-plane mr-1 font-size-14" />
            Ver webhooks
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
